import React, { useReducer, useRef } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Drawer, XStack } from '@usepepper/sprout-kit-ui';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { Platform, View, ViewStyle } from 'react-native';
import { SideDrawer } from '@experiences/components/side-drawer';

const PANEL_WIDTH = 400;
const ANIMATION_DURATION_MS = 200;

/**
 * Web-only style props (CSS `transition`, `willChange`) that aren't part of
 * `ViewStyle` but RN Web routes through to the underlying DOM node. Used as
 * the type for the hoisted transition constants below so we don't reach for
 * `as object`.
 */
interface WebTransitionStyle extends ViewStyle {
  transition?: string;
  willChange?: string;
}

// Hoisted style fragments — module-level constants so we don't allocate new
// objects per render. Tamagui diffs `enterStyle`/`exitStyle` by reference,
// so re-allocating each render would defeat its style cache.
const DRAWER_RIGHT_TRANSFORM = { x: PANEL_WIDTH };

const PANEL_WEB_TRANSITION: WebTransitionStyle | undefined =
  Platform.OS === 'web'
    ? {
        transition: `transform ${ANIMATION_DURATION_MS}ms ease-out`,
        willChange: 'transform',
      }
    : undefined;

const PANEL_TRANSLATE_OPEN = { translateX: 0 };
const PANEL_TRANSLATE_RIGHT_CLOSED = { translateX: PANEL_WIDTH };
const PANEL_TRANSLATE_LEFT_CLOSED = { translateX: -PANEL_WIDTH };

const NOOP = () => undefined;

/** Picks the panel's translateX based on side + open state. */
const getPanelTranslate = (isOpen: boolean, isRight: boolean) => {
  if (isOpen) {
    return PANEL_TRANSLATE_OPEN;
  }
  return isRight ? PANEL_TRANSLATE_RIGHT_CLOSED : PANEL_TRANSLATE_LEFT_CLOSED;
};

export type PanelSide = 'left' | 'right';

export interface ContentWithPanelTemplateProps {
  /** Main content (e.g. an order entry table) — renders flush at full width when the panel is closed. */
  children: React.ReactNode;
  /**
   * Invoked when the panel should close in overlay mode (backdrop tap or X press).
   * Push-content mode is closed by the caller setting `panelContent` to undefined.
   */
  onClose?: () => void;
  /** Side-panel content. Undefined means the panel is closed. */
  panelContent: React.ReactNode | undefined;
  /**
   * Which side of the screen the panel anchors to. Defaults to `'right'`
   * (item-detail panel use case). Use `'left'` for sidebar-style panels.
   */
  panelSide?: PanelSide;
}

/**
 * Generic responsive side-panel layout for an experience.
 *
 * Reads `layoutSize` from `useLayoutContext`:
 * - LARGE / MEDIUM (> 960px) → floating-panel mode. The panel is absolutely
 *   positioned on its anchor side at a fixed 400px width and slides in via
 *   `translateX`, floating on top of the main content (no backdrop). The
 *   main content keeps full width.
 * - SMALL_TABLET / SMALL (≤ 960px) → overlay mode. The main content keeps
 *   full width and the panel renders inside an SK-UI `Drawer` (web) or the
 *   `SideDrawer` (native — the SK-UI `Drawer` uses Tamagui translateX which
 *   crashes on RN, see sidebar-layout-shell.tsx for the same workaround).
 *
 * Panel width is fixed at 400px to keep a consistent panel experience.
 */
export const ContentWithPanelTemplate: React.FC<
  ContentWithPanelTemplateProps
> = ({ children, panelContent, onClose, panelSide = 'right' }) => {
  const [styles] = useStyles();
  const { layoutSize } = useLayoutContext();
  const isOverlayMode =
    layoutSize === LayoutSize.SMALL || layoutSize === LayoutSize.SMALL_TABLET;
  const isOpen = panelContent !== undefined;
  const isRight = panelSide === 'right';

  // Cache the most recent defined panel content so the close transition can
  // keep rendering it while the panel slides off-screen. The cached subtree
  // is dropped when the panel's close `transform` transition completes (see
  // `handlePanelTransitionEnd` below) — event-driven, no timer.
  const lastDefinedPanelContent = useRef<React.ReactNode>(panelContent);
  if (panelContent !== undefined) {
    lastDefinedPanelContent.current = panelContent;
  }
  // Bumped after we mutate the ref outside of render so the View re-reads
  // its content. (`useReducer` is the standard React idiom for a manual
  // re-render trigger; we don't actually need the counter value.)
  const [, forceRerender] = useReducer((tick: number) => tick + 1, 0);

  const handlePanelTransitionEnd = (event: { propertyName?: string }) => {
    if (
      panelContent === undefined &&
      event.propertyName === 'transform' &&
      lastDefinedPanelContent.current !== undefined
    ) {
      lastDefinedPanelContent.current = undefined;
      forceRerender();
    }
  };

  const renderedPanelContent = lastDefinedPanelContent.current;

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };

  if (isOverlayMode) {
    return (
      <View style={styles.overlayContainer}>
        <View style={styles.mainContent}>{children}</View>
        {Platform.OS === 'web' ? (
          <Drawer onOpenChange={handleDrawerOpenChange} open={isOpen}>
            <Drawer.Overlay />
            {/*
             * SK-UI `Drawer.Content` hard-codes `left: 0` + slide-in-from-left
             * in its base styled component. The wrapper spreads our props
             * after the hard-coded values, so passing `left='unset' right={0}`
             * and matching enter/exit transforms flips it to slide-from-right.
             */}
            {isRight ? (
              <Drawer.Content
                enterStyle={DRAWER_RIGHT_TRANSFORM}
                exitStyle={DRAWER_RIGHT_TRANSFORM}
                left="unset"
                right={0}
                width={PANEL_WIDTH}
              >
                <View style={styles.drawerContent}>{panelContent}</View>
              </Drawer.Content>
            ) : (
              <Drawer.Content width={PANEL_WIDTH}>
                <View style={styles.drawerContent}>{panelContent}</View>
              </Drawer.Content>
            )}
          </Drawer>
        ) : (
          <SideDrawer
            direction={panelSide}
            isOpen={isOpen}
            onClose={onClose ?? NOOP}
            showCloseButton={false}
            width={PANEL_WIDTH}
          >
            {panelContent}
          </SideDrawer>
        )}
      </View>
    );
  }

  const panelTranslate = getPanelTranslate(isOpen, isRight);

  return (
    <XStack flex={1} overflow="hidden" position="relative">
      <View style={styles.mainContent}>{children}</View>
      <View
        // RN Web passes `onTransitionEnd` through to the underlying DOM
        // node, but it's not in `ViewProps`'s TS types — spread to bypass.
        // Native has no CSS transition so this handler never fires there
        // (cached subtree stays mounted until reopen / unmount).
        {...(Platform.OS === 'web'
          ? { onTransitionEnd: handlePanelTransitionEnd }
          : {})}
        style={[
          isRight ? styles.panelContainerRight : styles.panelContainerLeft,
          { transform: [panelTranslate] },
          PANEL_WEB_TRANSITION,
        ]}
      >
        {renderedPanelContent}
      </View>
    </XStack>
  );
};

const useStyles = createStyles((theme) => ({
  drawerContent: {
    flex: 1,
  },
  mainContent: {
    flex: 1,
    minWidth: 0,
  },
  overlayContainer: {
    flex: 1,
  },
  // Panel is absolutely positioned at a fixed 400px width so its children
  // never reflow during the open/close transition. Two variants cover the
  // anchor side (left vs. right border).
  panelContainerLeft: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRightColor: theme.sk.color.divider.default,
    borderRightWidth: 1,
    bottom: 0,
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    width: PANEL_WIDTH,
  },
  panelContainerRight: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderLeftColor: theme.sk.color.divider.default,
    borderLeftWidth: 1,
    bottom: 0,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    top: 0,
    width: PANEL_WIDTH,
  },
}));
